// URL / MIME helpers — port of the private helpers at the bottom of
// android/.../ui/MainActivity.kt. Keeps the renderer components small and
// keeps the extension lists in one place when we add new formats.

import { resolveUrl } from './deviceApi'
import type { ContentItem } from './deviceApi'

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'avif', 'svg'])
const VIDEO_EXTS = new Set(['mp4', 'webm', 'ogg', 'mov', 'm4v'])
const PDF_EXTS   = new Set(['pdf'])

// Pre-cropped aspect variants land on ContentItem.variants when the upload
// went through the dashboard's MediaCropModal. The player picks the entry
// matching its screen orientation (vertical → 9:16, horizontal → 16:9) so
// the same source media renders correctly on every kiosk shape. When the
// requested key is missing — legacy uploads, GIFs, videos, PDFs — we fall
// back to the canonical `url` rather than failing the item.
export function pickVariantUrl(
  item: ContentItem,
  orientationType: 'horizontal' | 'vertical',
): string {
  const target = orientationType === 'vertical' ? '9:16' : '16:9'
  const v = item.variants?.[target]
  return v && v.length > 0 ? v : (item.url ?? '')
}

function ext(url: string | null | undefined): string {
  if (!url) return ''
  const path = url.split('?')[0]
  const dot  = path.lastIndexOf('.')
  return dot === -1 ? '' : path.slice(dot + 1).toLowerCase()
}

export function isImageUrl(url: string | null | undefined): boolean {
  return IMAGE_EXTS.has(ext(url))
}

export function isVideoUrl(url: string | null | undefined): boolean {
  return VIDEO_EXTS.has(ext(url))
}

// Raw PDF media added via the dashboard's "Document" upload mode. The dispatch
// routes these to PdfDisplay, which rasterizes + pages through the document
// with pdf.js (fullscreen, no viewer chrome) — matching the Android player's
// platform PdfRenderer.
export function isPdfUrl(url: string | null | undefined): boolean {
  return PDF_EXTS.has(ext(url))
}

/** Resolve a possibly-relative URL to an absolute one against the backend. */
export function absoluteUrl(url: string | null | undefined): string {
  return resolveUrl(url) ?? ''
}
